/**
 * ValidationTable.tsx — Per-row credential validation status table (D-05).
 *
 * Subscribes to WS `validation:progress` events and resolves each row live:
 *   pending → validating → valid (✓) / failed-with-reason (✗ + reason)
 *
 * Security (T-04-31):
 *   - Displays status only; NO credentials are rendered
 *   - Received events contain rowId + status + optional reason — never passwords
 */

import * as React from 'react';
import { useWsClient } from '../lib/WsProvider.js';
import type { ValidationProgress } from '@crm/types';
import type { OnboardingResultRow } from '@crm/types';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Badge,
} from '@crm/ui';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ValidationRow {
  rowIndex: number;
  /** The rowId from the WS event — used for matching live updates */
  rowId: string;
  accountId?: string;
  /** Display label (from the onboarding form — no passwords) */
  displayLabel: string;
  email: string;
  status: ValidationProgress['status'] | 'pending';
  reason?: string;
}

interface ValidationTableProps {
  /** batchId returned by POST /accounts or /accounts/bulk */
  batchId: string;
  /** Initial rows from the immediate POST response + display info */
  initialRows: Array<
    OnboardingResultRow & { displayLabel: string; email: string; rowId?: string }
  >;
}

// ---------------------------------------------------------------------------
// Status badge helper
// ---------------------------------------------------------------------------

function StatusBadge({
  status,
  reason,
}: {
  status: ValidationRow['status'];
  reason?: string;
}) {
  switch (status) {
    case 'valid':
      return <Badge variant="success">✓ Valid</Badge>;
    case 'invalid':
      return (
        <span className="flex items-center gap-1.5">
          <Badge variant="danger">✗ Invalid</Badge>
          {reason && <span className="text-xs text-text-muted">{reason}</span>}
        </span>
      );
    case 'rate_limited':
      return (
        <span className="flex items-center gap-1.5">
          <Badge variant="warning">Rate limited</Badge>
          {reason && <span className="text-xs text-text-muted">{reason}</span>}
        </span>
      );
    case 'network_error':
      return (
        <span className="flex items-center gap-1.5">
          <Badge variant="warning">Network error</Badge>
          {reason && <span className="text-xs text-text-muted">{reason}</span>}
        </span>
      );
    case 'validating':
      return (
        <span className="flex items-center gap-1.5">
          <Badge variant="info">
            <svg
              className="animate-spin h-2.5 w-2.5 mr-1"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              />
            </svg>
            Validating…
          </Badge>
        </span>
      );
    case 'pending':
    default:
      return <Badge variant="outline">Pending</Badge>;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ValidationTable({ batchId, initialRows }: ValidationTableProps) {
  const ws = useWsClient();

  /**
   * Maps rowId → live status.
   * WS events carry rowId (from the BullMQ job); we match on that
   * OR fall back to accountId when rowId is not available from the
   * initial REST response.
   */
  const [statuses, setStatuses] = React.useState<
    Map<string, { status: ValidationProgress['status']; reason?: string }>
  >(new Map());

  React.useEffect(() => {
    // Subscribe to validation:progress for THIS batchId only
    const unsub = ws.onValidationProgress((evt) => {
      if (evt.batchId !== batchId) return;
      setStatuses((prev) => {
        const next = new Map(prev);
        // Index by rowId; also index by accountId for lookup fallback
        next.set(evt.rowId, { status: evt.status, reason: evt.reason });
        if (evt.accountId) {
          next.set(evt.accountId, { status: evt.status, reason: evt.reason });
        }
        return next;
      });
    });
    return unsub;
  }, [ws, batchId]);

  // Merge initial rows with live statuses
  const rows: ValidationRow[] = initialRows.map((r) => {
    // Match by explicit rowId, then by accountId
    const key = r.rowId ?? r.accountId ?? String(r.rowIndex);
    const live = statuses.get(key);
    return {
      rowIndex: r.rowIndex,
      rowId: key,
      accountId: r.accountId,
      displayLabel: r.displayLabel,
      email: r.email,
      status: live?.status ?? (r.error ? 'invalid' : 'pending'),
      reason: live?.reason ?? r.error,
    };
  });

  const total = rows.length;
  const done = rows.filter(
    (r) =>
      r.status === 'valid' ||
      r.status === 'invalid' ||
      r.status === 'rate_limited' ||
      r.status === 'network_error',
  ).length;
  const valid = rows.filter((r) => r.status === 'valid').length;

  return (
    <div className="flex flex-col gap-3">
      {/* Summary bar */}
      <div className="flex items-center gap-4 text-xs text-text-muted">
        <span>
          {done} / {total} processed
        </span>
        <span className="text-success">{valid} valid</span>
        <span className="text-danger">{done - valid} failed</span>
        {done < total && (
          <span className="text-info">{total - done} pending</span>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8">#</TableHead>
            <TableHead>Label</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.rowId}>
              <TableCell className="text-text-muted">{row.rowIndex + 1}</TableCell>
              <TableCell>{row.displayLabel || '—'}</TableCell>
              <TableCell className="font-mono text-xs">{row.email}</TableCell>
              <TableCell>
                <StatusBadge status={row.status} reason={row.reason} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
